import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { IDeleteApiResponse, IError, apiLimit } from "../../utils/api.types";
import { VocuherCategory } from "../../utils/common-utils";
import {
  AReports,
  ReportFetch,
  ALedgerReports,
  LowStockResponse,
  RateListResponse,
  InventoryReportFetch,
  ItemReportByPartyResponse,
  ItemReportByPartyFetch,
  ItemPurchaseSummaryResponse,
  ItemPurchaseSummaryFetch,
  PartyWiseOutstandingResponse,
  PartyWiseOutstandingFetch,
  ItemSalesAndPurchaseSummaryResponse,
  ItemSalesAndPurchaseSummaryFetch,
  ProfitLossFetch,
  ProfitLossResponse,
  BalanceSheetFetch,
  BalanceSheetResponse,
  PartyBalancesResponse,
  PartyBalancesFetch,
  SearchPartiesResponse,
  SearchPartiesFetch
} from "../../utils/report.types";

/**
 * Fetch Invoices async thunk.
*/
export const fetchReport = createAsyncThunk(
    'report/fetchReport',
    async ({accountId, type, isShareExcel, params} : ReportFetch): Promise<AReports> => {
        let queryParam = (params)? `&${params}`: ''
        if(type) {
            queryParam += `&voucherCategory=${type}`
        }
        if(isShareExcel) {
            queryParam += `&isShareExcel=${isShareExcel}`
        }
        const response = await axios.get(`/api/v1/reports/sale-report?limit=50${queryParam}`);
        return response.data;
    }
);

export const downloadGstrReport = createAsyncThunk(
    'report/downloadGstrReport',
    async ({accountId, type, isDownloadPdf, params} : ReportFetch): Promise<any> => {
        let queryParam = (params)? `&${params}`: ''
        if(type) {
            queryParam += `&voucherCategory=${type}`
        }
        if(isDownloadPdf) {
            queryParam += `&isDownloadPdf=${isDownloadPdf}`
        }
        const response = await axios.get(`/api/v1/reports/sale-report?limit=50${queryParam}`, {
            responseType: "blob", // Ensure response is a Blob object
        });
        return response.data;
    }
);

export const downloadGstrExcelReport = createAsyncThunk(
    'report/downloadGstrExcelReport',
    async ({accountId, type, isDownloadExcel, params} : ReportFetch): Promise<any> => {
        let queryParam = (params)? `&${params}`: ''
        if(type) {
            queryParam += `&voucherCategory=${type}`
        }
        if(isDownloadExcel) {
            queryParam += `&isDownloadExcel=${isDownloadExcel}`
        }
        const response = await axios.get(`/api/v1/reports/sale-report?limit=50${queryParam}`, {
            responseType: "blob", // Ensure response is a Blob object
        });
        return response.data;
    }
);

export const fetchPartyLedgerReport = createAsyncThunk(
    'report/fetchPartyLedgerReport',
    async ({accountId, type, isShareExcel, params} : ReportFetch): Promise<ALedgerReports> => {
        let queryParam = (params)? `${params}`: ''
        if(isShareExcel) {
            queryParam += `&isShareExcel=${isShareExcel}`
        }
        const response = await axios.get(`/api/v1/reports/party-ledger?${queryParam}`);
        return response.data;
    }
);

export const downloadPartyLedgerReport = createAsyncThunk(
    'report/downloadPartyLedgerReport',
    async ({accountId, type, isDownloadExcel, isDownloadPdf, params} : ReportFetch): Promise<any> => {
        let queryParam = (params)? `${params}`: ''
        if(isDownloadPdf) {
            queryParam += `&isDownloadPdf=${isDownloadPdf}`
        }
        const response = await axios.get(`/api/v1/reports/party-ledger?${queryParam}`, {
            responseType: "blob", // Ensure response is a Blob object
        });
        return response.data;
    }
);

// TDS/TCS Report Thunks
export const fetchTdsPayableReport = createAsyncThunk(
    'report/fetchTdsPayableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-payable${queryParam}`);
        return response.data;
    }
);

export const downloadTdsPayableReport = createAsyncThunk(
    'report/downloadTdsPayableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-payable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const downloadTdsPayableExcelReport = createAsyncThunk(
    'report/downloadTdsPayableExcelReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = params ? `?${params}&isDownloadExcel=true` : '?isDownloadExcel=true'
        const response = await axios.get(`/api/v1/reports/tds-payable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const fetchTdsReceivableReport = createAsyncThunk(
    'report/fetchTdsReceivableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-receivable${queryParam}`);
        return response.data;
    }
);

export const downloadTdsReceivableReport = createAsyncThunk(
    'report/downloadTdsReceivableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-receivable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const downloadTdsReceivableExcelReport = createAsyncThunk(
    'report/downloadTdsReceivableExcelReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = params ? `?${params}&isDownloadExcel=true` : '?isDownloadExcel=true'
        const response = await axios.get(`/api/v1/reports/tds-receivable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const fetchTcsPayableReport = createAsyncThunk(
    'report/fetchTcsPayableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tcs-payable${queryParam}`);
        return response.data;
    }
);

export const fetchTcsReceivableReport = createAsyncThunk(
    'report/fetchTcsReceivableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tcs-receivable${queryParam}`);
        return response.data;
    }
);

// Low Stock Summary Report Thunks
export const fetchLowStockSummaryReport = createAsyncThunk(
    'report/fetchLowStockSummaryReport',
    async (params: InventoryReportFetch): Promise<LowStockResponse> => {
        const queryParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });
        
        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/low-stock-summary${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadLowStockSummaryReport = createAsyncThunk(
    'report/downloadLowStockSummaryReport',
    async (params: InventoryReportFetch): Promise<any> => {
        const queryParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });
        
        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/low-stock-summary${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Rate List Report Thunks
export const fetchRateListReport = createAsyncThunk(
    'report/fetchRateListReport',
    async (params: InventoryReportFetch): Promise<RateListResponse> => {
        const queryParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });
        
        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/rate-list${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadRateListReport = createAsyncThunk(
    'report/downloadRateListReport',
    async (params: InventoryReportFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/rate-list${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Item Report By Party Thunks
export const fetchItemReportByParty = createAsyncThunk(
    'report/fetchItemReportByParty',
    async (params: ItemReportByPartyFetch): Promise<ItemReportByPartyResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/item-report-by-party${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadItemReportByParty = createAsyncThunk(
    'report/downloadItemReportByParty',
    async (params: ItemReportByPartyFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/item-report-by-party${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Item Purchase Summary Report Thunks
export const fetchItemPurchaseSummary = createAsyncThunk(
    'report/fetchItemPurchaseSummary',
    async (params: ItemPurchaseSummaryFetch): Promise<ItemPurchaseSummaryResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/item-purchase-summary${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadItemPurchaseSummary = createAsyncThunk(
    'report/downloadItemPurchaseSummary',
    async (params: ItemPurchaseSummaryFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/item-purchase-summary${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Party Wise Outstanding Report Thunks
export const fetchPartyWiseOutstanding = createAsyncThunk(
    'report/fetchPartyWiseOutstanding',
    async (params: PartyWiseOutstandingFetch): Promise<PartyWiseOutstandingResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/party-wise-outstanding${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadPartyWiseOutstanding = createAsyncThunk(
    'report/downloadPartyWiseOutstanding',
    async (params: PartyWiseOutstandingFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/party-wise-outstanding${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Item Sales and Purchase Summary Report Thunks
export const fetchItemSalesAndPurchaseSummary = createAsyncThunk(
    'report/fetchItemSalesAndPurchaseSummary',
    async (params: ItemSalesAndPurchaseSummaryFetch): Promise<ItemSalesAndPurchaseSummaryResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/item-sales-and-purchase-summary${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadItemSalesAndPurchaseSummary = createAsyncThunk(
    'report/downloadItemSalesAndPurchaseSummary',
    async (params: ItemSalesAndPurchaseSummaryFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/item-sales-and-purchase-summary${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Profit & Loss Statement Thunks
export const fetchProfitLoss = createAsyncThunk(
    'report/fetchProfitLoss',
    async (params: ProfitLossFetch): Promise<ProfitLossResponse> => {
        let queryString = '';

        if (params.params) {
            // If params string is provided, use it directly
            queryString = params.params;
        } else {
            // Otherwise, build from individual parameters
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (key !== 'params' && value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, String(value));
                }
            });
            queryString = queryParams.toString();
        }

        const response = await axios.get(`/api/v1/reports/profit-loss${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadProfitLoss = createAsyncThunk(
    'report/downloadProfitLoss',
    async (params: ProfitLossFetch): Promise<any> => {
        let queryString = '';

        if (params.params) {
            // If params string is provided, use it directly
            queryString = params.params;
        } else {
            // Otherwise, build from individual parameters
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (key !== 'params' && value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, String(value));
                }
            });
            queryString = queryParams.toString();
        }

        const response = await axios.get(
            `/api/v1/reports/profit-loss${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Balance Sheet Thunks
export const fetchBalanceSheet = createAsyncThunk(
    'report/fetchBalanceSheet',
    async (params: BalanceSheetFetch): Promise<BalanceSheetResponse> => {
        let queryString = '';

        if (params.params) {
            // If params string is provided, use it directly
            queryString = params.params;
        } else {
            // Otherwise, build from individual parameters
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (key !== 'params' && value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, String(value));
                }
            });
            queryString = queryParams.toString();
        }

        const response = await axios.get(`/api/v1/reports/balance-sheet${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadBalanceSheet = createAsyncThunk(
    'report/downloadBalanceSheet',
    async (params: BalanceSheetFetch): Promise<any> => {
        let queryString = '';

        if (params.params) {
            // If params string is provided, use it directly
            queryString = params.params;
        } else {
            // Otherwise, build from individual parameters
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (key !== 'params' && value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, String(value));
                }
            });
            queryString = queryParams.toString();
        }

        const response = await axios.get(
            `/api/v1/reports/balance-sheet${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Balance Sheet with Party-wise breakdown Thunks
export const fetchBalanceSheetPartyWise = createAsyncThunk(
    'report/fetchBalanceSheetPartyWise',
    async (params: BalanceSheetFetch): Promise<BalanceSheetResponse> => {
        let queryString = '';

        if (params.params) {
            // If params string is provided, use it directly
            queryString = params.params;
        } else {
            // Otherwise, build from individual parameters
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (key !== 'params' && value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, String(value));
                }
            });
            queryString = queryParams.toString();
        }

        const response = await axios.get(`/api/v1/reports/balance-sheet-party-wise${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadBalanceSheetPartyWise = createAsyncThunk(
    'report/downloadBalanceSheetPartyWise',
    async (params: BalanceSheetFetch): Promise<any> => {
        let queryString = '';

        if (params.params) {
            // If params string is provided, use it directly
            queryString = params.params;
        } else {
            // Otherwise, build from individual parameters
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (key !== 'params' && value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, String(value));
                }
            });
            queryString = queryParams.toString();
        }

        const response = await axios.get(
            `/api/v1/reports/balance-sheet-party-wise${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Party Balances Thunks
export const fetchPartyBalances = createAsyncThunk(
    'report/fetchPartyBalances',
    async (params: PartyBalancesFetch): Promise<PartyBalancesResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/ledger/account/${params.accountId}/all-parties-balances${queryString ? `?${queryString}` : ''}`
        );
        return response.data;
    }
);

// Search Parties Thunk
export const searchParties = createAsyncThunk(
    'report/searchParties',
    async (params: SearchPartiesFetch): Promise<SearchPartiesResponse> => {
        const queryParams = new URLSearchParams();
        queryParams.append('query', params.query);

        if (params.limit) {
            queryParams.append('limit', String(params.limit));
        }

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/ledger/account/${params.accountId}/search-parties${queryString ? `?${queryString}` : ''}`
        );
        return response.data;
    }
);

// Expense Journal Entries Report Thunks (replaces Journal Entries Party Report)
export const fetchJournalEntriesPartyReport = createAsyncThunk(
    'report/fetchJournalEntriesPartyReport',
    async ({ accountId, params }: { accountId: string; params?: string }): Promise<any> => {
        const queryString = params ? `?${params}` : '';
        const response = await axios.get(`/api/v1/reports/journal-entries-party${queryString}`);
        return response.data;
    }
);

export const downloadJournalEntriesPartyReportPdf = createAsyncThunk(
    'report/downloadJournalEntriesPartyReportPdf',
    async ({ accountId, params }: { accountId: string; params: string }): Promise<any> => {
        const response = await axios.get(
            `/api/v1/reports/journal-entries-party?${params}`,
            {
                responseType: "blob"
            }
        );
        return response.data;
    }
);

export const downloadJournalEntriesPartyReportExcel = createAsyncThunk(
    'report/downloadJournalEntriesPartyReportExcel',
    async ({ accountId, params }: { accountId: string; params: string }): Promise<any> => {
        const response = await axios.get(
            `/api/v1/reports/journal-entries-party?${params}`,
            {
                responseType: "blob"
            }
        );
        return response.data;
    }
);

// Ledgers List Thunk - fetches ledgers used in journal entries for given date range
export const fetchAccountLedgers = createAsyncThunk(
    'report/fetchAccountLedgers',
    async ({ accountId, params }: { accountId: string; params?: string }): Promise<any> => {
        const queryString = params ? `?${params}` : '';
        const response = await axios.get(`/api/v1/reports/ledgers${queryString}`);
        return response.data;
    }
);

// Journal Entries Party-Wise Ledger Report Thunks
export const fetchJournalEntriesPartyLedgerReport = createAsyncThunk(
    'report/fetchJournalEntriesPartyLedgerReport',
    async ({ accountId, params }: { accountId: string; params?: string }): Promise<any> => {
        const queryString = params ? `?${params}` : '';
        const response = await axios.get(`/api/v1/reports/journal-entries-party-ledger${queryString}`);
        return response.data;
    }
);

export const downloadJournalEntriesPartyLedgerReportExcel = createAsyncThunk(
    'report/downloadJournalEntriesPartyLedgerReportExcel',
    async ({ accountId, params }: { accountId: string; params: string }): Promise<any> => {
        const response = await axios.get(
            `/api/v1/reports/journal-entries-party-ledger?${params}`,
            {
                responseType: "blob"
            }
        );
        return response.data;
    }
);

// Day Book Report Thunks
export const fetchDayBookReport = createAsyncThunk(
    'report/fetchDayBookReport',
    async ({ accountId, params }: { accountId: string; params?: string }): Promise<any> => {
        const queryString = params ? `?${params}` : '';
        const response = await axios.get(`/api/v1/reports/day-book${queryString}`);
        return response.data;
    }
);

export const downloadDayBookReportExcel = createAsyncThunk(
    'report/downloadDayBookReportExcel',
    async ({ accountId, params }: { accountId: string; params: string }): Promise<any> => {
        const response = await axios.get(
            `/api/v1/reports/day-book?${params}`,
            {
                responseType: "blob"
            }
        );
        return response.data;
    }
);

export const downloadDayBookReportPdf = createAsyncThunk(
    'report/downloadDayBookReportPdf',
    async ({ accountId, params }: { accountId: string; params: string }): Promise<any> => {
        const response = await axios.get(
            `/api/v1/reports/day-book?${params}`,
            {
                responseType: "blob"
            }
        );
        return response.data;
    }
);

// Cash and Bank Report (All Payments) Thunks
export const fetchCashAndBankReport = createAsyncThunk(
    'report/fetchCashAndBankReport',
    async ({ accountId, params }: { accountId: string; params?: string }): Promise<any> => {
        const queryString = params ? `?${params}` : '';
        const response = await axios.get(`/api/v1/reports/cash-and-bank-all-payments${queryString}`);
        return response.data;
    }
);

export const downloadCashAndBankReportExcel = createAsyncThunk(
    'report/downloadCashAndBankReportExcel',
    async ({ accountId, params }: { accountId: string; params: string }): Promise<any> => {
        const response = await axios.get(
            `/api/v1/reports/cash-and-bank-all-payments?${params}`,
            {
                responseType: "blob"
            }
        );
        return response.data;
    }
);

// Expense Category Report Thunks
export const fetchExpenseCategoryReport = createAsyncThunk(
    'report/fetchExpenseCategoryReport',
    async ({ accountId, params }: { accountId: string; params?: string }): Promise<any> => {
        const queryString = params ? `?${params}` : '';
        const response = await axios.get(`/api/v1/reports/expense-category${queryString}`);
        return response.data;
    }
);

export const downloadExpenseCategoryReportExcel = createAsyncThunk(
    'report/downloadExpenseCategoryReportExcel',
    async ({ accountId, params }: { accountId: string; params: string }): Promise<any> => {
        const response = await axios.get(
            `/api/v1/reports/expense-category?${params}`,
            {
                responseType: "blob"
            }
        );
        return response.data;
    }
);

// Party Ledger Transactions from Journal Entries Thunk
export const fetchPartyLedgerTransactions = createAsyncThunk(
    'report/fetchPartyLedgerTransactions',
    async (params: {
        partyId: string;
        partyType: 'client' | 'supplier';
        from: string;
        to: string;
        page?: number;
        limit?: number;
        voucherType?: string;
    }): Promise<any> => {
        const queryParams = new URLSearchParams();
        queryParams.append('partyType', params.partyType);
        queryParams.append('from', params.from);
        queryParams.append('to', params.to);

        if (params.page) queryParams.append('page', String(params.page));
        if (params.limit) queryParams.append('limit', String(params.limit));
        if (params.voucherType && params.voucherType !== 'all') {
            queryParams.append('voucherType', params.voucherType);
        }

        const response = await axios.get(
            `/api/v1/ledger/party/${params.partyId}/transactions?${queryParams.toString()}`
        );
        return response.data;
    }
);

// Fetch ALL Parties Ledger Transactions (for filtering)
export const fetchAllPartiesLedgerTransactions = createAsyncThunk(
    'report/fetchAllPartiesLedgerTransactions',
    async (params: {
        accountId: string;
        partyType: 'client' | 'supplier' | 'all';
        from: string;
        to: string;
        page?: number;
        limit?: number;
        voucherType?: string;
        partyId?: string;
    }): Promise<any> => {
        const queryParams = new URLSearchParams();
        queryParams.append('partyType', params.partyType);
        queryParams.append('from', params.from);
        queryParams.append('to', params.to);

        if (params.page) queryParams.append('page', String(params.page));
        if (params.limit) queryParams.append('limit', String(params.limit));
        if (params.voucherType && params.voucherType !== 'all') {
            queryParams.append('voucherType', params.voucherType);
        }
        if (params.partyId && params.partyId !== 'all') {
            queryParams.append('partyId', params.partyId);
        }

        const response = await axios.get(
            `/api/v1/ledger/account/${params.accountId}/all-parties/transactions?${queryParams.toString()}`
        );
        return response.data;
    }
);

// Employee Ledger Report Thunks
export const fetchEmployeeLedgerReport = createAsyncThunk(
    'report/fetchEmployeeLedgerReport',
    async ({ employeeId, startDate, endDate }: { employeeId: string; startDate?: string; endDate?: string }): Promise<any> => {
        let queryParam = `employeeId=${employeeId}`;
        if (startDate) queryParam += `&startDate=${startDate}`;
        if (endDate) queryParam += `&endDate=${endDate}`;
        const response = await axios.get(`/api/v1/employee-ledger?${queryParam}`);
        return response.data;
    }
);

export const downloadEmployeeLedgerPdf = createAsyncThunk(
    'report/downloadEmployeeLedgerPdf',
    async ({ employeeId, startDate, endDate }: { employeeId: string; startDate?: string; endDate?: string }): Promise<any> => {
        let queryParam = `employeeId=${employeeId}&isDownloadPdf=true`;
        if (startDate) queryParam += `&startDate=${startDate}`;
        if (endDate) queryParam += `&endDate=${endDate}`;
        const response = await axios.get(`/api/v1/employee-ledger?${queryParam}`, {
            responseType: "blob"
        });
        return response.data;
    }
);

/**
 * Fetch Cost Centre Report
 */
export const fetchCostCentreReport = createAsyncThunk(
    "reports/fetchCostCentreReport",
    async (filters: { startDate?: string; endDate?: string; costCentreId?: string; categoryId?: string }): Promise<any> => {
        let queryParam = '';
        const params = [];
        if (filters.startDate) params.push(`startDate=${filters.startDate}`);
        if (filters.endDate) params.push(`endDate=${filters.endDate}`);
        if (filters.costCentreId) params.push(`costCentreId=${filters.costCentreId}`);
        if (filters.categoryId) params.push(`categoryId=${filters.categoryId}`);

        if (params.length > 0) {
            queryParam = '?' + params.join('&');
        }

        const response = await axios.get(`/api/v1/reports/cost-centre${queryParam}`);
        return response.data;
    }
);

/**
 * Fetch Cost Centre Comparison
 */
export const fetchCostCentreComparison = createAsyncThunk(
    "reports/fetchCostCentreComparison",
    async (filters: {
        costCentreId: string;
        period1Start: string;
        period1End: string;
        period2Start: string;
        period2End: string;
    }): Promise<any> => {
        const queryParam = `?costCentreId=${filters.costCentreId}&period1Start=${filters.period1Start}&period1End=${filters.period1End}&period2Start=${filters.period2Start}&period2End=${filters.period2End}`;
        const response = await axios.get(`/api/v1/reports/cost-centre/comparison${queryParam}`);
        return response.data;
    }
);

/**
 * Download Cost Centre Report Excel
 */
export const downloadCostCentreReportExcel = createAsyncThunk(
    "reports/downloadCostCentreReportExcel",
    async (filters: { startDate?: string; endDate?: string; costCentreId?: string }): Promise<any> => {
        let queryParam = '';
        const params = [];
        if (filters.startDate) params.push(`startDate=${filters.startDate}`);
        if (filters.endDate) params.push(`endDate=${filters.endDate}`);
        if (filters.costCentreId) params.push(`costCentreId=${filters.costCentreId}`);

        if (params.length > 0) {
            queryParam = '?' + params.join('&');
        }

        const response = await axios.get(`/api/v1/reports/cost-centre/download${queryParam}`, {
            responseType: "blob"
        });
        return response.data;
    }
);

export const runMigrateJournalEntries = createAsyncThunk(
    "reports/runMigrateJournalEntries",
    async (payload: { fromDate: string; toDate: string; dryRun: boolean; module?: string }): Promise<any> => {
        const response = await axios.post('/api/v1/reports/migrate-journal-entries', payload);
        return response.data;
    }
);
